/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, computed, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {TranslateModule} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import {Subscription} from 'rxjs';
import {AppEventBusService} from '../events/app-event-bus.service';

import {deferSignalStateWrite} from '../util/template-state';
import template from './top-targeting-table.component.html';

import {OBJECT_TYPE} from '../constants/object-type';
import {StatsApiService} from './stats-api.service';
import {Downloader} from '../../../components/util/downloader';
import {formatPercentage as formatPercentageValue} from '../filters/format-percentage';
import {AppLocaleService} from '../locale/app-locale.service';
import {OgTranslateService} from '../filters/og-translate.service';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {ChartOptionsUtil} from '../options/chart-options-util.service';
import {APP_SHARED} from '../app-shared';
import {ColumnSelectorComponent} from './column-selector.component';

@Component({
  standalone: true,
  imports: [...APP_SHARED, TranslateModule, ColumnSelectorComponent],
  selector: 'adn-top-targeting-table',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopTargetingTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() object: any;
  @Input() parentType: string;
  @Input() topN: any;
  @Input() view: string;
  @Input() dateSelectorId: string;
  @Input() currency: any;

  sel: any = {};
  readonly semanticTargeting = signal<any>(null);
  readonly semanticTargetingList = signal<Array<{sentence: string; histogram: any[]; averageRenderedMatchScore?: number}>>([]);
  sorting = {
    orderByParam: 'impressions',
    asc: true
  };

  parentParam: string;
  readonly loaded = signal(false);
  readonly displayLoaded = computed(() => this.loaded());
  private destroyed = false;
  readonly error = signal(false);
  readonly dataTotals = signal<any[] | null>(null);
  readonly targetingExpression = signal(false);
  readonly startDate = signal<any>('UNDEFINED');
  readonly endDate = signal<any>(undefined);

  readonly starScores = [1, 2, 3, 4, 5];

  private chartDateListener: () => void;
  private allFields: any[];
  private chartOptionObject: any;
  private params: any = {};
  private configured = false;
  private statsSubscription?: Subscription;

  get resolvedParentType(): string {
    return this.parentType;
  }

  get resolvedDateSelectorId(): string {
    return this.dateSelectorId;
  }

  downloadFields: string[];
  downloadColumnOrder: string[];
  csvTableTitle: string;

  constructor(
    private readonly statsApi: StatsApiService,
    private readonly eventBus: AppEventBusService,
    private readonly adnLog: AdnLogService,
    private readonly translate: TranslateService,
    private readonly ogTranslate: OgTranslateService,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    private readonly appLocale: AppLocaleService
  ) {
  }

  ngOnInit(): void {
    this.tryInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.parentType) {
      this.tryInit();
    }
  }

  private tryInit(): void {
    this.chartOptionObject = OBJECT_TYPE[this.resolvedParentType];
    if (!this.chartOptionObject) {
      return;
    }
    if (this.configured) {
      return;
    }
    this.configured = true;
    this.initParams();

    const chartOptions = this.ChartOptionsUtil.getOptions(this.chartOptionObject.caps);
    this.parentParam = this.chartOptionObject.apiParam;
    this.allFields = chartOptions.targetingFields;

    const eventName = 'chartDates' + chartOptions.idKey + (this.resolvedDateSelectorId || '');
    const applyChartDates = (data: any) => {
      if (this.startDate() !== 'UNDEFINED' && moment(this.startDate()).isSame(data.startDate) && moment(this.endDate()).isSame(data.endDate)) {
        return;
      }
      // Defer date bindings — lastDates / chartDates can land during parent CD
      // ('UNDEFINED' → Date NG0100 on [adnStartDate]).
      deferSignalStateWrite(() => {
        if (this.destroyed) {
          return;
        }
        if (this.startDate() !== 'UNDEFINED' && moment(this.startDate()).isSame(data.startDate) && moment(this.endDate()).isSame(data.endDate)) {
          return;
        }
        this.initParams();

        this.startDate.set(data.startDate);
        this.endDate.set(data.endDate);

        this.loaded.set(false);
        this.dataTotals.set(null);
        this.error.set(false);
        this.targetingExpression.set(false);

        this.params.groupBy = null;

        if (this.startDate() && this.endDate()) {
          this.params.startDate = this.startDate();
          this.params.endDate = this.endDate();
        }

        this.statsSubscription?.unsubscribe();
        this.statsSubscription = this.statsApi.getTargeting(this.params).subscribe({next: (dataTotals: any) => {
          this.processTargetingData(dataTotals);
        }, error: (err: any) => {
          this.adnLog.warn(err);
          deferSignalStateWrite(() => {
            if (this.destroyed) {
              return;
            }
            this.error.set(true);
            this.loaded.set(true);
          });
        }});
      });
    };

    this.chartDateListener = this.eventBus.on(eventName, (_event: any, data: any) => {
      applyChartDates(data);
    });

    const lastDates = this.ChartOptionsUtil.getLastChartDates(eventName);
    if (lastDates) {
      applyChartDates(lastDates);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.chartDateListener) {
      this.chartDateListener();
    }
    this.statsSubscription?.unsubscribe();
  }

  fieldTranslate(key: string): string {
    return this.ogTranslate.transform(key);
  }

  formatPercentage(input: number, decimals?: number): string {
    return formatPercentageValue(input, decimals, undefined, this.appLocale.getLocaleId());
  }

  asPercent(ratio: number): number {
    return (ratio || 0) * 100;
  }

  orderedHistogram(sentenceStats: any): any[] {
    return _.orderBy(sentenceStats?.histogram || [], 'score');
  }

  sortUpdate(field: any): void {
    if (field.key === this.sorting.orderByParam) {
      this.sorting.asc = !this.sorting.asc;
      return;
    }
    this.sorting.orderByParam = field.key;
    this.sorting.asc = true;
  }

  sortedDataTotals(): any[] {
    const dataTotals = this.dataTotals();
    if (!dataTotals) {
      return [];
    }
    const key = this.sorting.orderByParam;
    // The old template's orderBy expression treated this flag as reverse.
    const reverse = this.sorting.asc;
    return _.orderBy(dataTotals, (row) => {
      const val = row[key];
      if (_.get(val, 'currency')) {
        return val.amount || -1;
      }
      if (_.get(val, 'count')) {
        return val.count || -1;
      }
      return val || -1;
    }, [reverse ? 'desc' : 'asc']);
  }

  toggleShowShort(dataRow: any): void {
    dataRow.showShort = !dataRow.showShort;
  }

  getRows(): any[] {
    return this.dataTotals() || [];
  }

  saveXls(): void {
    this.updateValues();
    new Downloader().saveXls2(this.csvTableTitle, this.downloadFields, this.downloadColumnOrder, this.dataTotals() || [], this.allFields);
  }

  saveDownloadFields(): void {
    this.updateValues();
  }

  private initParams(): void {
    if (!this.chartOptionObject || !this.object?.id) {
      return;
    }
    this.params = {
      id: this.object.id,
      timeout: 180000,
      idKey: this.chartOptionObject.apiParam + 'Id'
    };
  }

  private updateValues(): void {
    this.downloadFields = _.map(this.sel.fullFields, (f) => {
      return f.text || f.name;
    });
    this.downloadFields.unshift('Type', 'Targeting');
    this.downloadColumnOrder = _.map(this.sel.fullFields, (f) => {
      return f.key || f.id;
    });
    this.downloadColumnOrder.unshift('type', 'target');
    this.csvTableTitle = 'Targeting Totals';
  }

  private processTargetingData(data: any): void {
    let semanticTargeting: any = null;
    let targetingExpression = false;
    const dataTotals = _.map(data, (val, targeting) => {
      const expression = targeting.split('=');
      const targetType = expression[0];
      let targetDescription = expression.slice(1).join('=');
      const formattedData: any = {
        type: targetType
      };
      if (targetType === 'SEMANTIC_CATEGORY') {
        const sentence = targetDescription.split(':');
        if (sentence.length > 1) {
          const targetScore = sentence[0];
          const targetSentence = sentence[1];
          if (targetScore === 'SLIGHTLY_RELATED') {
            formattedData.score = 1;
          }
          if (targetScore === 'SOMEWHAT_RELATED') {
            formattedData.score = 2;
          }
          if (targetScore === 'MODERATELY_RELATED') {
            formattedData.score = 3;
          }
          if (targetScore === 'CLOSELY_RELATED') {
            formattedData.score = 4;
          }
          if (targetScore === 'VERY_CLOSELY_RELATED') {
            formattedData.score = 5;
          }
          if (formattedData.score || targetScore === 'NOT_RELATED' || targetScore === 'WEAKLY_RELATED') {
            targetDescription = sentence.slice(1).join(':');
          }

          semanticTargeting = semanticTargeting || {};
          semanticTargeting[targetSentence] = semanticTargeting[targetSentence] || {};
          semanticTargeting[targetSentence].histogram = semanticTargeting[targetSentence].histogram || [];
          semanticTargeting[targetSentence].histogram.push({
            score: formattedData.score,
            stats: val,
            sentence: targetSentence
          });
        }
      }
      formattedData.target = targetDescription;
      formattedData.targetShort = targetDescription.substring(0, 100);
      formattedData.targetLong = targetDescription.length >= 100;
      formattedData.showShort = formattedData.targetLong;
      if (formattedData.target) {
        targetingExpression = true;
      }
      _.forEach(val, (stat, keyStat) => {
        formattedData[keyStat] = stat;
      });
      return formattedData;
    });
    const sums = _.map(semanticTargeting, (sentenceStats, sentence) => {
      const sumRendered = _.reduce(sentenceStats.histogram, (num, smr) => {
        return num + smr.stats.rendered;
      }, 0);
      const sumRenderedMatchScore = _.reduce(sentenceStats.histogram, (num, histogramItem) => {
        return num + histogramItem.stats.renderedMatchScoreSum;
      }, 0);
      return {
        sentence: sentence,
        sumImpressions: _.reduce(sentenceStats.histogram, (num, histogramItem) => {
          return num + histogramItem.stats.impressions;
        }, 0),
        sumViewables: _.reduce(sentenceStats.histogram, (num, histogramItem) => {
          return num + histogramItem.stats.viewables;
        }, 0),
        sumRendered: sumRendered,
        sumClicks: _.reduce(sentenceStats.histogram, (num, histogramItem) => {
          return num + histogramItem.stats.clicks;
        }, 0),
        averageRenderedMatchScore: sumRendered === 0 ? 0 : (sumRenderedMatchScore / sumRendered) * 10 - 2
      };
    });
    _.forEach(semanticTargeting, (sentenceStats, sentence) => {
      const sum = _.find(sums, (s) => {
        return s.sentence === sentence;
      });
      if (sum) {
        _.forEach(sentenceStats.histogram, (histogramItem) => {
          histogramItem.ratioImpressions = histogramItem.stats.impressions / sum.sumImpressions;
          histogramItem.ratioViewables = histogramItem.stats.viewables / sum.sumViewables;
          histogramItem.ratioRendered = histogramItem.stats.rendered / sum.sumRendered;
          histogramItem.ratioClicks = histogramItem.stats.clicks / sum.sumClicks;
        });
        sentenceStats.averageRenderedMatchScore = sum.averageRenderedMatchScore;
      }
    });
    const semanticTargetingList = semanticTargeting
      ? _.map(semanticTargeting, (sentenceStats, sentence) => {
        return {
          sentence: sentence as string,
          histogram: sentenceStats.histogram,
          averageRenderedMatchScore: sentenceStats.averageRenderedMatchScore
        };
      })
      : [];
    deferSignalStateWrite(() => {
      if (this.destroyed) {
        return;
      }
      this.semanticTargeting.set(semanticTargeting);
      this.dataTotals.set(dataTotals);
      this.targetingExpression.set(targetingExpression);
      this.semanticTargetingList.set(semanticTargetingList);
      this.loaded.set(true);
    });
  }
}
